import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { accommodationTypesAPI, languageSupportAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import { canManageAccommodation, canDeleteAccommodation } from '../../utils/roleUtils';
import browserGlobal from '../../utils/browserGlobal';

const defaultTranslation = () => ({ languageCode: 'en', name: '' });

function getTranslationRowKey(translation, index) {
  return `${translation.languageCode || 'new'}-${index}`;
}

function getSubmitButtonLabel(isSubmitting, editMode, translate) {
  if (isSubmitting) {
    return translate('common.saving');
  }
  if (editMode) {
    return translate('common.update');
  }
  return translate('common.add');
}

function normalizeItemTranslations(translations) {
  if (translations?.length) {
    return translations.map((tr) => ({
      languageCode: tr.languageCode || '',
      name: tr.name || '',
    }));
  }
  return [defaultTranslation()];
}

function formatTranslationsCell(translations) {
  if (translations?.length) {
    return translations.map((tr) => `${tr.languageCode}: ${tr.name}`).join(', ');
  }
  return '-';
}

function hasListItems(list) {
  return Array.isArray(list) && list.length > 0;
}

const AccommodationTypes = () => {
  const { t } = useLanguage();
  const adminUser = getAdminUser();
  const canAddEdit = canManageAccommodation(adminUser?.role);
  const canDelete = canDeleteAccommodation(adminUser?.role);

  const [list, setList] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    id: null,
    name: '',
    translations: [defaultTranslation()],
  });
  const [languages, setLanguages] = useState([]);

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  const fetchLanguages = async () => {
    try {
      const response = await languageSupportAPI.getLanguages(0, 500);
      if (response?.data?.success === true && Array.isArray(response?.data?.payload)) {
        const languageList = (response.data.payload ?? []).filter((l) => l.isActive !== false);
        setLanguages(languageList);
      } else {
        setLanguages([]);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      setLanguages([]);
    }
  };

  const fetchList = useCallback(
    async (opts = {}) => {
      const isCancelled = typeof opts.isCancelled === 'function' ? opts.isCancelled : () => false;
      setIsLoading(true);
      try {
        const response = await accommodationTypesAPI.getAccommodationTypes(page, rowsPerPage);
        if (isCancelled()) return;

        if (response?.data?.success === true) {
          const payload = Array.isArray(response?.data?.payload) ? response.data.payload : [];
          const apiTotal = response?.data?.totalRecord;
          const total = apiTotal != null && Number(apiTotal) >= 0 ? Number(apiTotal) : payload.length;
          setList(total === 0 ? [] : payload.slice(0, total));
          setTotalRecord(total);
        } else {
          setList([]);
          setTotalRecord(0);
        }
      } catch (error) {
        if (isCancelled()) return;
        console.error('Error fetching accommodation types:', error);
        setList([]);
        setTotalRecord(0);
      } finally {
        if (!isCancelled()) {
          setIsLoading(false);
        }
      }
    },
    [page, rowsPerPage]
  );

  useEffect(() => {
    let cancelled = false;
    fetchList({ isCancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
  }, [fetchList]);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const handleOpen = (item = null) => {
    if (item) {
      setFormData({
        id: item.id,
        name: item.name || '',
        translations: normalizeItemTranslations(item.translations),
      });
      setEditMode(true);
    } else {
      setFormData({
        id: null,
        name: '',
        translations: [defaultTranslation()],
      });
      setEditMode(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
  };

  const handleTranslationChange = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.translations];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, translations: next };
    });
  };

  const addTranslation = () => {
    setFormData((prev) => ({
      ...prev,
      translations: [...prev.translations, defaultTranslation()],
    }));
  };

  const removeTranslation = (index) => {
    if (formData.translations.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    if (!formData.name?.trim()) {
      showToast(t('accTypes.toastNameRequired'), 'warning');
      return false;
    }
    for (let i = 0; i < formData.translations.length; i++) {
      const tr = formData.translations[i];
      if (!tr.languageCode?.trim()) {
        showToast(t('accTypes.translationLangRequired').replace('{n}', String(i + 1)), 'warning');
        return false;
      }
      if (!tr.name?.trim()) {
        showToast(t('accTypes.translationNameRequired').replace('{n}', String(i + 1)), 'warning');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const translations = formData.translations.map((t) => ({
      languageCode: t.languageCode.trim(),
      name: t.name.trim(),
    }));

    const payload = editMode && formData.id != null
      ? { id: formData.id, name: formData.name.trim(), translations }
      : { name: formData.name.trim(), translations };

    setIsSubmitting(true);
    try {
      if (editMode) {
        await accommodationTypesAPI.updateAccommodationType(payload);
        showToast(t('accTypes.updated'), 'success');
      } else {
        await accommodationTypesAPI.createAccommodationType(payload);
        showToast(t('accTypes.created'), 'success');
      }
      await fetchList();
      handleClose();
    } catch (error) {
      console.error('Error saving accommodation type:', error);
      showToast(error?.response?.data?.errorMessage || t('accTypes.saveError'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!browserGlobal.confirm(t('accTypes.confirmDelete'))) return;
    try {
      await accommodationTypesAPI.deleteAccommodationType(id);
      showToast(t('accTypes.deleted'), 'success');
      await fetchList();
    } catch (error) {
      console.error('Error deleting accommodation type:', error);
      showToast(error?.response?.data?.errorMessage || t('accTypes.deleteError'), 'error');
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(Number.parseInt(e.target.value, 10));
    setPage(0);
  };

  const renderTableBodyContent = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }

    if (!hasListItems(list)) {
      return (
        <TableRow>
          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
            {t('accTypes.empty')}
          </TableCell>
        </TableRow>
      );
    }

    return list.map((row) => (
      <TableRow key={row.id}>
        <TableCell>{row.id}</TableCell>
        <TableCell>{row.name}</TableCell>
        <TableCell>{formatTranslationsCell(row.translations)}</TableCell>
        <TableCell align="right">
          <IconButton
            size="small"
            onClick={() => handleOpen(row)}
            sx={{ color: 'primary.main', mr: 1 }}
          >
            <Edit />
          </IconButton>
          {canDelete && (
            <IconButton
              size="small"
              onClick={() => handleDelete(row.id)}
              sx={{ color: 'error.main' }}
            >
              <Delete />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1.5, sm: 0 }}
      >
        <Typography variant="h4" color="primary">
          {t('accTypes.title')}
        </Typography>
        {canAddEdit && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('accTypes.add')}
          </Button>
        )}
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          mt: 3,
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
          borderRadius: 2,
          '& .MuiTableCell-head': {
            backgroundColor: '#ebf4f4',
            color: '#0F3D3E',
            fontWeight: 600,
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: '#ebf4f4',
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('accTypes.colId')}</TableCell>
              <TableCell>{t('accTypes.colName')}</TableCell>
              <TableCell>{t('accTypes.colTranslations')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTableBodyContent()}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalRecord}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? t('accTypes.dialogEdit') : t('accTypes.dialogAdd')}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label={t('common.name')}
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={t('accTypes.fieldNamePh')}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('accTypes.translationsHint')}
                </Typography>
                {formData.translations.map((tr, index) => (
                  <Box
                    key={getTranslationRowKey(tr, index)}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <FormControl required size="small" sx={{ flex: 1 }}>
                      <InputLabel id={`lang-select-${index}`}>{t('accTypes.fieldLanguage')}</InputLabel>
                      <Select
                        labelId={`lang-select-${index}`}
                        label={t('accTypes.fieldLanguage')}
                        value={tr.languageCode || ''}
                        onChange={(e) => handleTranslationChange(index, 'languageCode', e.target.value)}
                      >
                        
                        {languages.map((lang) => (
                          <MenuItem key={lang.id || lang.languageCode} value={lang.languageCode || ''}>
                            {lang.name || lang.languageCode || ''}
                          </MenuItem>
                        ))}
                        {tr.languageCode && !languages.some((l) => (l.languageCode || '') === tr.languageCode) && (
                          <MenuItem value={tr.languageCode}>{tr.languageCode}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <TextField
                      required
                      size="small"
                      label={t('common.name')}
                      value={tr.name}
                      onChange={(e) => handleTranslationChange(index, 'name', e.target.value)}
                      placeholder={t('accTypes.namePlaceholder')}
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeTranslation(index)}
                      disabled={formData.translations.length <= 1}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={addTranslation}
                  sx={{ mt: 1 }}
                >
                  {t('accTypes.addTranslation')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
            }}
          >
            {getSubmitButtonLabel(isSubmitting, editMode, t)}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccommodationTypes;
